import { GraphBuilder } from '../graph/builder';
import { Shape } from '../graph/types';
import { ComponentDef } from './types';

export function buildAttentionSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const qkNorm = params.qk_norm === 'true';

  const qId = `${prefix}q`;
  const kId = `${prefix}k`;
  const vId = `${prefix}v`;
  const splitQ = `${prefix}split_q`;
  const splitK = `${prefix}split_k`;
  const splitV = `${prefix}split_v`;
  const kt = `${prefix}kt`;
  const qk = `${prefix}qk`;
  const scale = `${prefix}scale`;
  const mask = `${prefix}mask`;
  const softmax = `${prefix}softmax`;
  const attnV = `${prefix}attn_v`;
  const concat = `${prefix}concat`;
  const wo = `${prefix}wo`;

  g.linear(qId, inputId, [d_model, d_model]);
  g.linear(kId, inputId, [d_model, d_model]);
  g.linear(vId, inputId, [d_model, d_model]);

  const splitShape: Shape = [n_heads, seq_len, d_head];
  g.reshape(splitQ, qId, splitShape);
  g.reshape(splitK, kId, splitShape);
  g.reshape(splitV, vId, splitShape);

  let qForAttn = splitQ;
  let kForAttn = splitK;
  if (qkNorm) {
    const normQ = `${prefix}norm_q`;
    const normK = `${prefix}norm_k`;
    g.norm(normQ, splitQ, 'RMSNorm');
    g.norm(normK, splitK, 'RMSNorm');
    qForAttn = normQ;
    kForAttn = normK;
  }

  g.transpose(kt, kForAttn, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(qk, qForAttn, kt, [n_heads, seq_len, seq_len]);
  g.scale(scale, qk, `1/√d_k`);
  g.mask(mask, scale);
  g.softmax(softmax, mask, -1);
  g.matmul(attnV, softmax, splitV, [n_heads, seq_len, d_head]);
  g.reshape(concat, attnV, [seq_len, d_model]);
  g.linear(wo, concat, [d_model, d_model]);

  return wo;
}

function buildBidirectionalSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;

  g.linear(`${prefix}q`, inputId, [d_model, d_model]);
  g.linear(`${prefix}k`, inputId, [d_model, d_model]);
  g.linear(`${prefix}v`, inputId, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_k`, `${prefix}k`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_v`, `${prefix}v`, [n_heads, seq_len, d_head]);

  g.transpose(`${prefix}kt`, `${prefix}split_k`, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}split_q`, `${prefix}kt`, [n_heads, seq_len, seq_len]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');
  // No causal mask — full bidirectional attention (BERT/encoder style)
  g.softmax(`${prefix}softmax`, `${prefix}scale`, -1);
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}split_v`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}concat`, `${prefix}attn_v`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);

  return `${prefix}wo`;
}

function buildCrossAttentionSubgraph(
  g: GraphBuilder,
  queryInputId: string,
  kvInputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;

  // Q from decoder (query_seq_len), K/V from encoder (kv_seq_len)
  g.linear(`${prefix}q`, queryInputId, [d_model, d_model]);
  g.linear(`${prefix}k`, kvInputId, [d_model, d_model]);
  g.linear(`${prefix}v`, kvInputId, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_k`, `${prefix}k`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_v`, `${prefix}v`, [n_heads, seq_len, d_head]);

  g.transpose(`${prefix}kt`, `${prefix}split_k`, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}split_q`, `${prefix}kt`, [n_heads, seq_len, seq_len]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');
  // No causal mask in cross-attention (decoder attends to all encoder tokens)
  g.softmax(`${prefix}softmax`, `${prefix}scale`, -1);
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}split_v`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}concat`, `${prefix}attn_v`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);

  return `${prefix}wo`;
}

export function buildGQASubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>,
  isMQA = false
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const n_kv_heads = isMQA ? 1 : Math.max(1, Math.floor(n_heads / 4));

  const qId = `${prefix}q`;
  const kId = `${prefix}k`;
  const vId = `${prefix}v`;
  const splitQ = `${prefix}split_q`;
  const splitK = `${prefix}split_k`;
  const splitV = `${prefix}split_v`;
  const expandK = `${prefix}expand_k`;
  const expandV = `${prefix}expand_v`;
  const kt = `${prefix}kt`;
  const qk = `${prefix}qk`;
  const scale = `${prefix}scale`;
  const mask = `${prefix}mask`;
  const softmax = `${prefix}softmax`;
  const attnV = `${prefix}attn_v`;
  const concat = `${prefix}concat`;
  const wo = `${prefix}wo`;

  g.linear(qId, inputId, [d_model, n_heads * d_head]);
  g.linear(kId, inputId, [d_model, n_kv_heads * d_head]);
  g.linear(vId, inputId, [d_model, n_kv_heads * d_head]);

  g.reshape(splitQ, qId, [n_heads, seq_len, d_head]);
  g.reshape(splitK, kId, [n_kv_heads, seq_len, d_head]);
  g.reshape(splitV, vId, [n_kv_heads, seq_len, d_head]);

  g.reshape(expandK, splitK, [n_heads, seq_len, d_head]);
  g.reshape(expandV, splitV, [n_heads, seq_len, d_head]);

  g.transpose(kt, expandK, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(qk, splitQ, kt, [n_heads, seq_len, seq_len]);
  g.scale(scale, qk, `1/√d_k`);
  g.mask(mask, scale);
  g.softmax(softmax, mask, -1);
  g.matmul(attnV, softmax, expandV, [n_heads, seq_len, d_head]);
  g.reshape(concat, attnV, [seq_len, d_model]);
  g.linear(wo, concat, [d_model, d_model]);

  return wo;
}

function buildMLASubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const d_c_kv = Math.max(1, Math.floor(d_model / 4));   // compressed KV latent dim
  const d_c_q  = Math.max(1, Math.floor(d_model / 2));   // compressed Q latent dim
  const d_r    = Math.max(1, Math.floor(d_head / 2));     // decoupled RoPE dim per head

  // ── Q path: compress → decompress + RoPE branch ──
  g.linear(`${prefix}q_down`, inputId, [d_model, d_c_q]);
  g.norm(`${prefix}q_norm`, `${prefix}q_down`, 'RMSNorm');
  g.linear(`${prefix}q_up`, `${prefix}q_norm`, [d_c_q, n_heads * (d_head + d_r)]);
  g.reshape(`${prefix}q_full`, `${prefix}q_up`, [n_heads, seq_len, d_head + d_r]);
  // split nope (content) and rope (positional) portions of Q
  g.reshape(`${prefix}q_nope`, `${prefix}q_full`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}q_rope`, `${prefix}q_full`, [n_heads, seq_len, d_r]);
  g.activation(`${prefix}q_rope_out`, `${prefix}q_rope`, 'RoPE');

  // ── KV path: single compressed latent c_KV ──
  g.linear(`${prefix}kv_down`, inputId, [d_model, d_c_kv]);
  g.norm(`${prefix}kv_norm`, `${prefix}kv_down`, 'RMSNorm');
  // k_nope (content) + k_rope (decoupled positional) + v
  g.linear(`${prefix}k_up`, `${prefix}kv_norm`, [d_c_kv, n_heads * d_head]);
  g.linear(`${prefix}v_up`, `${prefix}kv_norm`, [d_c_kv, n_heads * d_head]);
  g.linear(`${prefix}k_rope_proj`, inputId, [d_model, n_heads * d_r]);

  g.reshape(`${prefix}k_nope`, `${prefix}k_up`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}k_rope`, `${prefix}k_rope_proj`, [n_heads, seq_len, d_r]);
  g.activation(`${prefix}k_rope_out`, `${prefix}k_rope`, 'RoPE');
  g.reshape(`${prefix}v`, `${prefix}v_up`, [n_heads, seq_len, d_head]);

  // ── Combine Q and K for attention: concat content + rope dims ──
  g.concat(`${prefix}q_cat`, [`${prefix}q_nope`, `${prefix}q_rope_out`], [n_heads, seq_len, d_head + d_r]);
  g.concat(`${prefix}k_cat`, [`${prefix}k_nope`, `${prefix}k_rope_out`], [n_heads, seq_len, d_head + d_r]);

  g.transpose(`${prefix}kt`, `${prefix}k_cat`, [n_heads, d_head + d_r, seq_len], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}q_cat`, `${prefix}kt`, [n_heads, seq_len, seq_len]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');
  g.mask(`${prefix}mask`, `${prefix}scale`);
  g.softmax(`${prefix}softmax`, `${prefix}mask`, -1);
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}v`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}concat`, `${prefix}attn_v`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);

  return `${prefix}wo`;
}

function buildLinearAttentionSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;

  const qId = `${prefix}q`;
  const kId = `${prefix}k`;
  const vId = `${prefix}v`;
  const splitQ = `${prefix}split_q`;
  const splitK = `${prefix}split_k`;
  const splitV = `${prefix}split_v`;
  const featQ = `${prefix}feat_q`;
  const featK = `${prefix}feat_k`;
  const kt = `${prefix}kt`;
  const kv = `${prefix}kv`;
  const out = `${prefix}out`;
  const wo = `${prefix}wo`;

  g.linear(qId, inputId, [d_model, d_model]);
  g.linear(kId, inputId, [d_model, d_model]);
  g.linear(vId, inputId, [d_model, d_model]);

  const splitShape: Shape = [n_heads, seq_len, d_head];
  g.reshape(splitQ, qId, splitShape);
  g.reshape(splitK, kId, splitShape);
  g.reshape(splitV, vId, splitShape);

  g.activation(featQ, splitQ, 'Feature(Q)');
  g.activation(featK, splitK, 'Feature(K)');

  g.transpose(kt, featK, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(kv, kt, splitV, [n_heads, d_head, d_head]);
  g.matmul(out, featQ, kv, [n_heads, seq_len, d_head]);
  g.reshape(out, out, [seq_len, d_model]);
  g.linear(wo, out, [d_model, d_model]);

  return wo;
}

function buildSlidingWindowSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const window_size = Math.min(128, Math.floor(seq_len / 4));

  const qId = `${prefix}q`;
  const kId = `${prefix}k`;
  const vId = `${prefix}v`;
  const splitQ = `${prefix}split_q`;
  const splitK = `${prefix}split_k`;
  const splitV = `${prefix}split_v`;
  const kt = `${prefix}kt`;
  const qk = `${prefix}qk`;
  const scale = `${prefix}scale`;
  const win_mask = `${prefix}win_mask`;
  const softmax = `${prefix}softmax`;
  const attnV = `${prefix}attn_v`;
  const concat = `${prefix}concat`;
  const wo = `${prefix}wo`;

  g.linear(qId, inputId, [d_model, d_model]);
  g.linear(kId, inputId, [d_model, d_model]);
  g.linear(vId, inputId, [d_model, d_model]);

  g.reshape(splitQ, qId, [n_heads, seq_len, d_head]);
  g.reshape(splitK, kId, [n_heads, seq_len, d_head]);
  g.reshape(splitV, vId, [n_heads, seq_len, d_head]);

  g.transpose(kt, splitK, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(qk, splitQ, kt, [n_heads, seq_len, seq_len]);
  g.scale(scale, qk, '1/√d_k');
  // Sliding window mask: token i only attends to [i-w, i], O(n·w) complexity
  g.mask(win_mask, scale);
  g.scale(win_mask, win_mask, `window w=${window_size}  (banded mask)`);
  g.softmax(softmax, win_mask, -1);
  g.matmul(attnV, softmax, splitV, [n_heads, seq_len, d_head]);
  g.reshape(concat, attnV, [seq_len, d_model]);
  g.linear(wo, concat, [d_model, d_model]);
  return wo;
}

function buildSparseSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const block_size = 64;

  const qId = `${prefix}q`;
  const kId = `${prefix}k`;
  const vId = `${prefix}v`;
  const splitQ = `${prefix}split_q`;
  const splitK = `${prefix}split_k`;
  const splitV = `${prefix}split_v`;
  const kt = `${prefix}kt`;
  const qk = `${prefix}qk`;
  const scale = `${prefix}scale`;
  const sparse_mask = `${prefix}sparse_mask`;
  const softmax = `${prefix}softmax`;
  const attnV = `${prefix}attn_v`;
  const concat = `${prefix}concat`;
  const wo = `${prefix}wo`;

  g.linear(qId, inputId, [d_model, d_model]);
  g.linear(kId, inputId, [d_model, d_model]);
  g.linear(vId, inputId, [d_model, d_model]);

  g.reshape(splitQ, qId, [n_heads, seq_len, d_head]);
  g.reshape(splitK, kId, [n_heads, seq_len, d_head]);
  g.reshape(splitV, vId, [n_heads, seq_len, d_head]);

  g.transpose(kt, splitK, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(qk, splitQ, kt, [n_heads, seq_len, seq_len]);
  g.scale(scale, qk, '1/√d_k');
  // Block-sparse mask: local blocks + random long-range + global tokens
  g.mask(sparse_mask, scale);
  g.scale(sparse_mask, sparse_mask, `block=${block_size}  local+random+global`);
  g.softmax(softmax, sparse_mask, -1);
  g.matmul(attnV, softmax, splitV, [n_heads, seq_len, d_head]);
  g.reshape(concat, attnV, [seq_len, d_model]);
  g.linear(wo, concat, [d_model, d_model]);
  return wo;
}

function buildRingAttentionSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const P = 4; // number of ranks
  const local_seq = Math.floor(seq_len / P);

  // Split sequence across P ranks
  g.split(`${prefix}seq_split`, inputId, [P, local_seq, d_model], `P=${P} ranks`);

  // Local QKV projection on each shard
  g.linear(`${prefix}q`, `${prefix}seq_split`, [d_model, d_model]);
  g.linear(`${prefix}k`, `${prefix}seq_split`, [d_model, d_model]);
  g.linear(`${prefix}v`, `${prefix}seq_split`, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, local_seq, d_head]);
  g.reshape(`${prefix}split_k`, `${prefix}k`, [n_heads, local_seq, d_head]);
  g.reshape(`${prefix}split_v`, `${prefix}v`, [n_heads, local_seq, d_head]);

  // Local attention on initial shard
  g.transpose(`${prefix}kt`, `${prefix}split_k`, [n_heads, d_head, local_seq], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}split_q`, `${prefix}kt`, [n_heads, local_seq, local_seq]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');
  g.mask(`${prefix}mask`, `${prefix}scale`);
  g.softmax(`${prefix}softmax`, `${prefix}mask`, -1);
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}split_v`, [n_heads, local_seq, d_head]);

  // P2P ring-pass: rotate K/V blocks across ranks, accumulate partial outputs
  g.p2p(`${prefix}ring_pass`, `${prefix}split_v`, [n_heads, local_seq, d_head]);
  g.add(`${prefix}accum`, `${prefix}attn_v`, `${prefix}ring_pass`);

  // Allreduce to combine partial outputs across ranks
  g.allreduce(`${prefix}allreduce`, `${prefix}accum`, [n_heads, local_seq, d_head]);

  g.reshape(`${prefix}concat`, `${prefix}allreduce`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);
  return `${prefix}wo`;
}

function buildRopeAlibiSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;

  g.linear(`${prefix}q`, inputId, [d_model, d_model]);
  g.linear(`${prefix}k`, inputId, [d_model, d_model]);
  g.linear(`${prefix}v`, inputId, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_k`, `${prefix}k`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_v`, `${prefix}v`, [n_heads, seq_len, d_head]);

  // Apply RoPE rotation to Q and K pairs
  g.activation(`${prefix}rope_q`, `${prefix}split_q`, 'RoPE');
  g.activation(`${prefix}rope_k`, `${prefix}split_k`, 'RoPE');

  g.transpose(`${prefix}kt`, `${prefix}rope_k`, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}rope_q`, `${prefix}kt`, [n_heads, seq_len, seq_len]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');

  // Add ALiBi slope bias to attention scores before softmax
  g.scale(`${prefix}alibi`, `${prefix}scale`, '+ ALiBi slope bias');

  g.mask(`${prefix}mask`, `${prefix}alibi`);
  g.softmax(`${prefix}softmax`, `${prefix}mask`, -1);
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}split_v`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}concat`, `${prefix}attn_v`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);
  return `${prefix}wo`;
}

function buildFlashAttentionSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const block_size = 64; // SRAM tile size

  g.linear(`${prefix}q`, inputId, [d_model, d_model]);
  g.linear(`${prefix}k`, inputId, [d_model, d_model]);
  g.linear(`${prefix}v`, inputId, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_k`, `${prefix}k`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}split_v`, `${prefix}v`, [n_heads, seq_len, d_head]);

  // Tile Q into SRAM blocks
  g.split(`${prefix}q_tile`, `${prefix}split_q`, [n_heads, Math.ceil(seq_len / block_size), block_size, d_head], `tile B=${block_size}`);

  // Tile K into SRAM blocks
  g.split(`${prefix}k_tile`, `${prefix}split_k`, [n_heads, Math.ceil(seq_len / block_size), block_size, d_head], `tile B=${block_size}`);

  // Tile V into SRAM blocks
  g.split(`${prefix}v_tile`, `${prefix}split_v`, [n_heads, Math.ceil(seq_len / block_size), block_size, d_head], `tile B=${block_size}`);

  // Block-level QK^T within SRAM tiles
  g.transpose(`${prefix}kt_tile`, `${prefix}k_tile`, [n_heads, Math.ceil(seq_len / block_size), d_head, block_size], 'K^T tile');
  g.matmul(`${prefix}qk_tile`, `${prefix}q_tile`, `${prefix}kt_tile`, [n_heads, Math.ceil(seq_len / block_size), block_size, block_size]);
  g.scale(`${prefix}s_tile`, `${prefix}qk_tile`, '1/√d_k');
  g.mask(`${prefix}mask_tile`, `${prefix}s_tile`);

  // Online softmax: running max + running sum for numerical stability
  g.scale(`${prefix}online_max`, `${prefix}mask_tile`, 'running max (online softmax)');
  g.softmax(`${prefix}p_tile`, `${prefix}online_max`, -1);

  // Block-level attention output: P_tile * V_tile
  g.matmul(`${prefix}o_tile`, `${prefix}p_tile`, `${prefix}v_tile`, [n_heads, Math.ceil(seq_len / block_size), block_size, d_head]);

  // Accumulate block-level outputs with running sum rescaling
  g.scale(`${prefix}rescale`, `${prefix}o_tile`, 'rescale by running sum');
  g.reshape(`${prefix}concat`, `${prefix}rescale`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);
  return `${prefix}wo`;
}

function buildPagedAttentionSubgraph(
  g: GraphBuilder,
  inputId: string,
  prefix: string,
  params: Record<string, number | string>
): string {
  const d_model = Number(params.d_model);
  const n_heads = Number(params.n_heads);
  const seq_len = Number(params.seq_len);
  const d_head = d_model / n_heads;
  const page_size = 16; // tokens per KV page (vLLM style)
  const n_pages = Math.ceil(seq_len / page_size);

  g.linear(`${prefix}q`, inputId, [d_model, d_model]);
  g.linear(`${prefix}k`, inputId, [d_model, d_model]);
  g.linear(`${prefix}v`, inputId, [d_model, d_model]);

  g.reshape(`${prefix}split_q`, `${prefix}q`, [n_heads, seq_len, d_head]);

  // Paged KV blocks: store K/V in non-contiguous physical pages
  g.reshape(`${prefix}k_pages`, `${prefix}k`, [n_pages, page_size, n_heads, d_head]);
  g.reshape(`${prefix}v_pages`, `${prefix}v`, [n_pages, page_size, n_heads, d_head]);

  // Cache paged KV blocks
  g.cache(`${prefix}kv_cache`, `${prefix}k_pages`, [n_pages, page_size, n_heads, d_head]);

  // Block table lookup: map logical token positions to physical page indices
  g.reshape(`${prefix}block_table`, `${prefix}kv_cache`, [n_heads, seq_len, d_head]);

  g.transpose(`${prefix}kt`, `${prefix}block_table`, [n_heads, d_head, seq_len], 'K^T');
  g.matmul(`${prefix}qk`, `${prefix}split_q`, `${prefix}kt`, [n_heads, seq_len, seq_len]);
  g.scale(`${prefix}scale`, `${prefix}qk`, '1/√d_k');
  g.mask(`${prefix}mask`, `${prefix}scale`);
  g.softmax(`${prefix}softmax`, `${prefix}mask`, -1);

  // Attention over non-contiguous KV pages via block table
  g.matmul(`${prefix}attn_v`, `${prefix}softmax`, `${prefix}block_table`, [n_heads, seq_len, d_head]);
  g.reshape(`${prefix}concat`, `${prefix}attn_v`, [seq_len, d_model]);
  g.linear(`${prefix}wo`, `${prefix}concat`, [d_model, d_model]);
  return `${prefix}wo`;
}

export const attentionComponent: ComponentDef = {
  id: 'attention',
  name: 'Attention',
  description: 'Transformer attention variants: MHA, GQA, MQA, MLA (DeepSeek), Linear, Sliding Window, Sparse, Cross-Attention, Bidirectional, Ring, RoPE·ALiBi, Flash, and Paged.',
  category: 'component',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'n_heads', label: 'Heads', type: 'number', default: 8, min: 1, max: 128, step: 1 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 128, min: 1, max: 8192, step: 1 },
    {
      name: 'variant',
      label: 'Algorithm variant',
      type: 'select',
      default: 'standard',
      options: [
        { label: 'MHA (Standard)', value: 'standard' },
        { label: 'GQA (Grouped Query)', value: 'gqa' },
        { label: 'MQA (Multi-Query)', value: 'mqa' },
        { label: 'MLA (DeepSeek Latent)', value: 'mla' },
        { label: 'Linear Attention', value: 'linear-attention' },
        { label: 'Sliding Window (Mistral)', value: 'sliding-window' },
        { label: 'Sparse (BigBird/Longformer)', value: 'sparse' },
        { label: 'Cross-Attention (Encoder→Decoder)', value: 'cross-attention' },
        { label: 'Bidirectional (BERT/Encoder)', value: 'bidirectional' },
        { label: 'Ring Attention', value: 'ring' },
        { label: 'RoPE·ALiBi', value: 'rope_alibi' },
        { label: 'Flash Attention', value: 'flash' },
        { label: 'Paged Attention', value: 'paged' }
      ]
    },
    {
      name: 'qk_norm',
      label: 'QK-Norm (normalize Q,K)',
      type: 'select',
      default: 'false',
      options: [
        { label: 'Off', value: 'false' },
        { label: 'On (RMSNorm before QK matmul)', value: 'true' }
      ]
    }
  ],
  buildGraph: (params) => {
    const g = new GraphBuilder();
    const seq_len = Number(params.seq_len);
    const d_model = Number(params.d_model);
    g.input('x', [seq_len, d_model]);

    const variant = String(params.variant || 'standard');
    let out: string;
    if (variant === 'gqa') {
      out = buildGQASubgraph(g, 'x', '', params, false);
    } else if (variant === 'mqa') {
      out = buildGQASubgraph(g, 'x', '', params, true);
    } else if (variant === 'mla') {
      out = buildMLASubgraph(g, 'x', '', params);
    } else if (variant === 'linear-attention') {
      out = buildLinearAttentionSubgraph(g, 'x', '', params);
    } else if (variant === 'sliding-window') {
      out = buildSlidingWindowSubgraph(g, 'x', '', params);
    } else if (variant === 'sparse') {
      out = buildSparseSubgraph(g, 'x', '', params);
    } else if (variant === 'cross-attention') {
      // Cross-attention: decoder queries attend to encoder key/value
      g.input('encoder_out', [seq_len, d_model]);
      out = buildCrossAttentionSubgraph(g, 'x', 'encoder_out', '', params);
    } else if (variant === 'bidirectional') {
      out = buildBidirectionalSubgraph(g, 'x', '', params);
    } else if (variant === 'ring') {
      out = buildRingAttentionSubgraph(g, 'x', '', params);
    } else if (variant === 'rope_alibi') {
      out = buildRopeAlibiSubgraph(g, 'x', '', params);
    } else if (variant === 'flash') {
      out = buildFlashAttentionSubgraph(g, 'x', '', params);
    } else if (variant === 'paged') {
      out = buildPagedAttentionSubgraph(g, 'x', '', params);
    } else {
      out = buildAttentionSubgraph(g, 'x', '', params);
    }

    g.output('out', out);
    return g.build();
  }
};
